// The schemas are a set of types, each associated with a set of properties.
// Schema are used to improve the way search engines read and represent our page.
// We use Schema in the JSON+LD format.
// You can read more at https://schema.org

import { PDPPageReview } from 'utils/types';

export type ProductDataSchema = {
  productId: number;
  name: string;
  slug: string;
  sku: string;
  upc: string;
  price: string;
  powerReviews: {
    average_rating: number;
    review_count: number;
    rating_histogram: number[];
    recommended_ratio: number;
    contents: PDPPageReview[];
  };
  outOfStock: boolean;
  promotionable: boolean;
};

interface questionJSONLDSchema {
  '@type': SchemaType;
  name: string;
  acceptedAnswer: {
    '@type': SchemaType;
    text: string;
  };
}

interface ListItemJSONLDSchema {
  '@type': SchemaType;
  position: number;
  url: string;
}

export enum SchemaType {
  ORGANIZATION = 'Organization',
  FAQ_PAGE = 'FAQPage',
  PRODUCT = 'Product',
  OFFER = 'Offer',
  LIST_ITEM = 'ListItem',
  BRAND = 'Brand',
  AGGREGATE_RATING = 'AggregateRating',
  ITEM_LIST = 'ItemList',
  QUESTION = 'Question',
  ANSWER = 'Answer',
  PERSON = 'Person',
  REVIEW = 'Review',
  RATING = 'Rating',
  LOCATION_CREATED = 'AdministrativeArea',
  POSTAL_ADDRESS = 'PostalAddress',
  CONTACT_POINT = 'ContactPoint',
  GEO_COORDINATES = 'GeoCoordinates',
  OPENING_HOURS_SPECIFICATION = 'OpeningHoursSpecification',
  MEDICAL_CLINIC = 'MedicalClinic',
}

export enum Availability {
  OUT_OF_STOCK = 'https://schema.org/OutOfStock',
  IN_STOCK = 'https://schema.org/InStock',
}

/**
 * Structured Data for Reviews
 * This schema represents a review from an user.
 */
export interface ReviewJSONLDSchema {
  '@type': SchemaType;
  name: string;
  reviewBody: string;
  datePublished: string;
  author: {
    '@type': SchemaType;
    name: string;
  };
  locationCreated: {
    '@type': SchemaType;
    name: string;
  };
  reviewRating: {
    '@type': SchemaType;
    ratingValue: number;
  };
}

/**
 * Structured data required by crawlers to match
 * data in Google Merchant feed. Please note that
 * SKU in this context is meant to match productId
 * rather than our internal use of "sku"; gtin12 is
 * a 12 character string representing our product
 * UPC codes.
 */
export interface ProductJSONLDSchema {
  '@context': string;
  '@type': SchemaType;
  name: string;
  brand: {
    '@type': SchemaType;
    name: string;
  };
  description: string;
  sku: string;
  gtin12: string | null;
  url: string;
  image: string | undefined;
  aggregateRating?: {
    '@type': SchemaType;
    ratingValue?: number;
    reviewCount?: number;
  };
  review: ReviewJSONLDSchema[];
  offers: {
    '@type': SchemaType;
    sku: string;
    url: string;
    priceValidUntil: string;
    availability: Availability;
    price: string;
    priceCurrency: string;
  };
}

/**
 * Structured data in format recommended by Google for
 * summary listing pages (used in our case for the PIP)
 * https://developers.google.com/search/docs/advanced/structured-data/carousel#summary-page
 */
export interface ItemListJSONLDSchema {
  '@context': string;
  '@type': SchemaType;
  itemListElement: ListItemJSONLDSchema[];
}

export interface FaqJSONLDSchema {
  '@context': string;
  '@type': SchemaType;
  mainEntity: questionJSONLDSchema[];
}

export interface OpeningHoursSpecification {
  '@type': SchemaType;
  dayOfWeek: string | string[];
  opens: string | null;
  closes: string | null;
}

export interface OrganizationJSONLDSchema {
  '@context': string;
  '@type': SchemaType;
  name: string;
  legalName: string;
  url: string;
  logo: string;
  foundingDate: string;
  founders: {
    '@type': SchemaType;
    name: string;
  };
  address: {
    '@type': SchemaType;
    streetAddress: string;
    addressLocality: string;
    addressRegion: string;
    postalCode: string;
    addressCountry: string;
  };
  geo?: {
    '@type': SchemaType;
    latitude: number;
    longitude: number;
  };
  contactPoint: {
    '@type': SchemaType;
    contactType: string;
    email: string;
  };
  sameAs: string[];
  openingHoursSpecification?: OpeningHoursSpecification[];
}

export type JSONLDSchema =
  | ProductJSONLDSchema
  | ItemListJSONLDSchema
  | FaqJSONLDSchema
  | OrganizationJSONLDSchema;
